import { Elements } from '@kontent-ai/delivery-sdk/lib/elements';
import {
  PortableText,
  PortableTextMarkComponentProps,
  PortableTextReactComponents,
  PortableTextTypeComponentProps,
  toPlainText,
} from '@portabletext/react';
import {
  browserParse,
  nodeParse,
  transformToPortableText,
  PortableTextComponent,
  PortableTextImage,
  PortableTextExternalLink,
  PortableTextInternalLink,
  resolveTable,
  PortableTextTable,
} from '@kontent-ai/rich-text-resolver';
import { AsyncComponent } from '@r-and-a-shared-ui/core-component-config';
import {
  ImageSchemaOrgModel,
  SchemaOrgContext,
  SchemaOrgTypes,
  getPrefixedMembershipInternalLink,
  replacePlaceholders,
  getProjectType,
  ProjectTypes,
} from '@r-and-a-shared-ui/utils';
import { Link } from '../Link/Link';
import { Image } from '../Image/Image';
import {
  Attributes,
  AUTH_CONTEXT_KEYS,
  MembershipTypes,
  useAuthContext,
} from '@r-and-a-shared-ui/auth-client';
import dayjs from 'dayjs';

import classnames from 'classnames';
import { CustomWindow } from '../Cookies/CookiesPopup';
import stylesPP from './scss/RichTextPP.module.scss';
import stylesMMB from './scss/RichTextMMB.module.scss';

declare let window: CustomWindow;

const isPPproject = getProjectType() === ProjectTypes.PlayerPortals;
const styles = isPPproject ? stylesPP : stylesMMB;

export interface RichTextProps {
  content?: Elements.RichTextElement;
  membershipType?: string;
  kontentItemId?: string;
  textSize?: string;
}

const RichTextComponentBuilder =
  (richTextElement: Elements.RichTextElement) =>
  // eslint-disable-next-line react/display-name
  ({ value }: PortableTextTypeComponentProps<PortableTextComponent>) => {
    const item = richTextElement?.linkedItems?.find(
      item => item.system.codename === value.component._ref,
    );

    return item ? (
      <AsyncComponent
        kontentItem={item}
        contentType={item.system.type as any}
        key={item.system.id}
      />
    ) : (
      ''
    );
  };

const createPortableTextComponent = (
  richTextElement: Elements.RichTextElement,
): Partial<PortableTextReactComponents> => {
  return {
    types: {
      component: RichTextComponentBuilder(richTextElement),
      image: ({ value }: PortableTextTypeComponentProps<PortableTextImage>) => {
        const schemaOrgContent: ImageSchemaOrgModel = {
          '@type': SchemaOrgTypes.IMAGE_OBJECT,
          '@context': SchemaOrgContext,
          contentUrl: value?.asset?.url,
        };

        return (
          <Image
            imageUrl={value?.asset?.url}
            alt={value?.asset?.alt || 'Image'}
            width={value?.asset?.width ?? 1400}
            height={value?.asset?.height ?? 768}
            schemaOrgContent={schemaOrgContent}
          />
        );
      },
      table: ({ value }: PortableTextTypeComponentProps<PortableTextTable>) => {
        const tableString = resolveTable(value, toPlainText);
        return (
          <div
            className={styles['table-responsive']}
            dangerouslySetInnerHTML={{ __html: tableString }}
          />
        );
      },
    },
    marks: {
      link: ({
        value,
        children,
      }: PortableTextMarkComponentProps<PortableTextExternalLink>) => {
        const target = value?.target ? (value?.target as string) : undefined;
        const isCookieControl =
          value?.href === 'javascript:CookieControl.open()';

        return (
          <a
            href={isCookieControl ? '#' : value?.href}
            target={target}
            rel={value?.rel}
            title={value?.title}
            data-new-window={value?.['data-new-window']}
            onClick={e => {
              if (isCookieControl) {
                e.preventDefault();
                window.CookieControl?.open();
              }
            }}
          >
            {children}
          </a>
        );
      },
      internalLink: ({
        value,
        children,
      }: PortableTextMarkComponentProps<PortableTextInternalLink>) => {
        const item = richTextElement.links.find(
          item => item.linkId === value?.reference._ref,
        );
        const href = getPrefixedMembershipInternalLink(
          item?.type,
          item?.urlSlug,
        );

        return (
          <Link href={href || ''} internal title="">
            {children}
          </Link>
        );
      },
    },
  };
};

const useReplacePlaceholders = (
  richText?: Elements.RichTextElement,
  membershipType?: string,
) => {
  const projectType = getProjectType();
  const isPP = projectType === ProjectTypes.PlayerPortals;
  const { userState } = useAuthContext(
    isPP
      ? AUTH_CONTEXT_KEYS.PP_CONTEXT_KEY
      : AUTH_CONTEXT_KEYS.AUTH_CONTEXT_KEY,
  );

  const { user } = userState;
  const membership =
    !isPP && 'membership' in userState ? userState.membership : null;

  if (!richText || !membershipType) {
    return richText;
  }

  if (
    membershipType.toLocaleUpperCase() !== membership?.type?.toLocaleUpperCase()
  ) {
    return undefined;
  }

  if (
    membershipType.toLocaleUpperCase() ===
    MembershipTypes.None.toLocaleUpperCase()
  ) {
    return richText;
  }

  const memberName = user?.[Attributes.GivenName] || '';
  const formattedDateSinceMember = membership.startedAt
    ? dayjs(membership.startedAt).format('MMMM YYYY')
    : '2024';

  const parsedText = replacePlaceholders(richText.value, {
    memberName,
    formattedDateSinceMember,
  });

  return {
    ...richText,
    value: parsedText,
  } as Elements.RichTextElement;
};

const useParseTree = (richText?: Elements.RichTextElement) => {
  if (!richText || !richText.value)
    return {
      portableText: null,
      portableTextComponent: null,
    };
  const treeParser = typeof window !== 'undefined' ? browserParse : nodeParse;
  const parsedTree = treeParser(richText.value);
  const portableText = parsedTree ? transformToPortableText(parsedTree) : null;
  const portableTextComponents = createPortableTextComponent(richText);

  return {
    portableText,
    portableTextComponents,
  };
};

export function RichText(props: RichTextProps) {
  const { content, membershipType, kontentItemId, textSize } = props;

  const richText = useReplacePlaceholders(content, membershipType);
  const { portableText, portableTextComponents } = useParseTree(richText);

  if (!portableText) {
    return null;
  }

  const textSizeClass = textSize ? styles[`text-size--${textSize}`] : undefined;

  return (
    <div
      className={classnames(styles['container'], textSizeClass)}
      data-kontent-item-id={kontentItemId}
    >
      <PortableText value={portableText} components={portableTextComponents} />
    </div>
  );
}

export default RichText;
